<template>
  <div class="step-review">
    <div class="step-header">
      <h2>{{ t('Revisão e Confirmação') }}</h2>
      <p class="step-description">
        {{ isReadonlyMode ? t('Visualize os dados completos da regra de pagamento.') : t('Por favor, revise todos os detalhes da regra antes de criar.') }}
      </p>
    </div>

    <!-- READONLY MODE Banner - Rules cannot be edited -->
    <inline-message v-if="isReadonlyMode" severity="warn" :closable="false" class="readonly-banner">
      <div class="banner-content">
        <i class="pi pi-lock" style="font-size: 1.5rem;"></i>
        <div>
          <strong>{{ t('Atenção! Regras de pagamento não podem ser editadas') }}</strong>
          <p>{{ t('Para alterar esta regra, desative-a e crie uma nova regra com os dados desejados.') }}</p>
        </div>
      </div>
    </inline-message>

    <!-- Rule Summary Card -->
    <div class="rule-summary">
      <div class="summary-header">
        <i class="pi pi-file-text"></i>
        <h3>{{ t('Resumo da Regra') }}</h3>
      </div>

      <div class="summary-content">
        <div class="summary-row">
          <span class="summary-label">{{ t('Funcionário:') }}</span>
          <span class="summary-value">{{ employeeName }}</span>
        </div>
        <div class="summary-row">
          <span class="summary-label">{{ t('Franquia:') }}</span>
          <span class="summary-value">{{ franchiseName }}</span>
        </div>
        <div class="summary-row">
          <span class="summary-label">{{ t('Tipo de Regra:') }}</span>
          <span class="summary-value rule-type-badge">{{ getRuleTypeLabel() }}</span>
        </div>

        <div v-if="wizardData.rule_type === 'fixed'" class="summary-details">
          <div class="summary-row">
            <span class="summary-label">{{ t('Valor Fixo:') }}</span>
            <span class="summary-value amount">{{ n(wizardData.amount || 0, 'currency') }}</span>
          </div>
          <div class="summary-row">
            <span class="summary-label">{{ t('Tipo de Período:') }}</span>
            <span class="summary-value">{{ getPeriodTypeLabel() }}</span>
          </div>
        </div>

        <div v-else class="summary-details">
          <div class="summary-row">
            <span class="summary-label">{{ t('Estrutura:') }}</span>
            <span class="summary-value">{{ getCommissionStructureLabel() }}</span>
          </div>

          <div v-if="wizardData.commission_structure === 'simple'" class="summary-row">
            <span class="summary-label">{{ t('Percentual:') }}</span>
            <span class="summary-value amount">{{ n((wizardData.amount || 0) * 100, 'percent') }}</span>
          </div>

          <div v-else class="summary-brackets">
            <div class="summary-label">{{ t('Faixas de Comissão:') }}</div>
            <div class="brackets-list">
              <div v-for="(bracket, index) in brackets" :key="index" class="bracket-item">
                <span class="bracket-range">
                  {{ n(bracket.min, 'currency') }}
                  <template v-if="bracket.max">
                    {{ t('até') }} {{ n(bracket.max, 'currency') }}
                  </template>
                  <template v-else>
                    {{ t('ou mais') }}
                  </template>
                </span>
                <span class="bracket-rate">{{ n(bracket.rate, 'percent') }}</span>
              </div>
            </div>
          </div>
        </div>

        <div v-if="wizardData.description" class="summary-description">
          <div class="summary-label">{{ t('Descrição:') }}</div>
          <div class="description-content" v-html="sanitizeHtmlContent(wizardData.description)"></div>
        </div>
      </div>
    </div>

    <!-- Action Buttons in Readonly Mode -->
    <div v-if="isReadonlyMode" class="action-buttons">
      <Button
        data-testid="payment-rule-desativar-btn"
        :label="t('Desativar Regra')"
        icon="pi pi-trash"
        severity="danger"
        outlined
        @click="handleDeactivate"
      />
      <Button
        data-testid="payment-rule-fechar-btn"
        :label="t('Fechar')"
        icon="pi pi-times"
        severity="secondary"
        @click="handleClose"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue';
import type { Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { sanitizeHtmlContent } from '../../../../utils/sanitizeHtmlContent';
import { useI18n as useI18nFormatter } from 'vue-i18n';
import { permissionsService } from '@/services/permissionsService';
import { usePaymentRules } from '@/composables/usePaymentRules';
import { useConfirmAction } from '@/composables/useConfirmAction';
import { log } from '@/utils/DebugLogger';
import { eventBus } from '@/eventBus';

type Id = number | string;
type CommissionStructureType = 'simple' | 'tiered' | 'progressive';

interface Bracket {
  min: number;
  max: number | null;
  /** Rate stored as fraction (0..1). PRESERVED. */
  rate: number;
  ratePercent?: number;
}

interface WizardData {
  franchise_id?: Id;
  user_id?: Id;
  rule_type?: string;
  commission_structure?: CommissionStructureType;
  amount?: number;
  period_type?: string;
  description?: string;
  meta?: string | null;
  [key: string]: unknown;
}

interface ExistingRule {
  id: Id;
  [key: string]: unknown;
}

interface WizardMachine {
  wizardData: Ref<WizardData>;
  isReadonlyMode?: Ref<boolean>;
  isEditMode?: Ref<boolean>;
  existingRule?: Ref<ExistingRule | null>;
  cancel?: () => void;
}

interface FranchiseOption {
  ID: Id;
  Name: string;
}

interface EmployeeOption {
  ID: Id;
  Name: string;
}

// Inject machine from parent
const machine = inject<WizardMachine | null>('wizardMachine', null);

const { t } = useI18n();
const { n } = useI18nFormatter();
const { deactivateRule } = usePaymentRules();
const { confirmAction } = useConfirmAction();

// Use wizard data from machine
const wizardData = computed<WizardData>(() => machine?.wizardData.value || {});
const isReadonlyMode = computed<boolean>(() => machine?.isReadonlyMode?.value || false);
const isEditMode = computed<boolean>(() => machine?.isEditMode?.value || false);
const existingRule = computed<ExistingRule | null>(() => {
  return machine?.existingRule?.value || null;
});

const employeeName = computed<string>(() => {
  if (!wizardData.value.user_id) return t('Não selecionado');
  const franchises = permissionsService.getFranquiasSelectArray() as FranchiseOption[];
  for (const franchise of franchises) {
    // Resolução de NOME (inclui inativos) — não é um select.
    const funcionarios = permissionsService.getFuncionariosByFranquiaID(Number(franchise.ID));
    const employee = funcionarios[String(wizardData.value.user_id)];
    if (employee) return employee.funcionario_nome;
  }
  return t('Não encontrado');
});

const franchiseName = computed<string>(() => {
  if (!wizardData.value.franchise_id) return t('Não selecionada');
  const franchises = permissionsService.getFranquiasSelectArray() as FranchiseOption[];
  const franchise = franchises.find(f => f.ID === wizardData.value.franchise_id);
  return franchise ? franchise.Name : t('Não encontrada');
});

const brackets = computed<Bracket[]>(() => {
  if (wizardData.value.meta) {
    try {
      const meta = JSON.parse(wizardData.value.meta);
      if (meta.commission_structure?.brackets) {
        // PRESERVED: rate kept as fraction; ratePercent derived for display only
        return (meta.commission_structure.brackets as Bracket[]).map(b => ({
          ...b,
          ratePercent: Math.round(b.rate * 100 * 100) / 100
        }));
      }
    } catch (e) {
      log('Step5Review', 'brackets', 'Error parsing brackets from meta', e);
    }
  }

  // Fallback: if no meta but we have a non-simple commission structure, provide default brackets
  // PRESERVED default brackets — keep numeric values exact.
  if (wizardData.value.commission_structure && wizardData.value.commission_structure !== 'simple') {
    log('Step5Review', 'commissionBrackets', 'No brackets in meta, commission_structure', wizardData.value.commission_structure);
    return [
      { min: 0, max: 29999.99, rate: 0.03, ratePercent: 3 },
      { min: 30000, max: null, rate: 0.05, ratePercent: 5 }
    ];
  }

  return [];
});

const getRuleTypeLabel = (): string => {
  // PRESERVED: discriminator strings
  const labels: Record<string, string> = {
    'fixed': t('Salário Fixo'),
    'commission_over_gross_revenue_executed': t('Comissão sobre Serviços Executados'),
    'commission_over_gross_revenue_sold_remotely': t('Comissão sobre Vendas Remotas'),
    'commission_over_gross_revenue_sold_in_loco': t('Comissão sobre Vendas Presenciais')
  };
  const ruleType = wizardData.value.rule_type;
  return (ruleType && labels[ruleType]) || ruleType || '';
};

const getPeriodTypeLabel = (): string => {
  const labels: Record<string, string> = {
    'weekly': t('Semanal'),
    'biweekly': t('Quinzenal'),
    'monthly': t('Mensal')
  };
  const periodType = wizardData.value.period_type;
  return (periodType && labels[periodType]) || periodType || '';
};

const getCommissionStructureLabel = (): string => {
  const labels: Record<string, string> = {
    'simple': t('Simples (taxa única)'),
    'tiered': t('Escalonada (taxa única no limite)'),
    'progressive': t('Progressiva (como IR)')
  };
  const cs = wizardData.value.commission_structure;
  return (cs && labels[cs]) || cs || '';
};

const handleDeactivate = (): void => {
  if (!existingRule.value) {
    log('Step5Review', 'handleDeactivate', 'No existing rule to deactivate');
    return;
  }
  const ruleId = existingRule.value.id;

  confirmAction({
    message: t('Tem certeza que deseja desativar esta regra de pagamento?'),
    header: t('Confirmação'),
    onAccept: async () => {
      try {
        log('Step5Review', 'handleDeactivate', 'Deactivating rule', { ruleId });
        await deactivateRule(Number(ruleId));

        // Emit success event
        eventBus.emit('payment-rule.deactivated', ruleId);
        eventBus.emit('globalMessage.success', {
          message: t('Regra de pagamento desativada com sucesso'),
          duration: 3000
        });

        // Close modal
        machine?.cancel?.();
      } catch (error) {
        const err = error as Error;
        log('Step5Review', 'handleDeactivate', 'Error deactivating payment rule', err);
        eventBus.emit('globalMessage.error', {
          message: err.message || t('Falha ao desativar regra de pagamento'),
          duration: 5000
        });
      }
    }
  });
};

const handleClose = (): void => {
  machine?.cancel?.();
};
</script>

<style scoped>
.step-review {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.step-header h2 {
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-color);
}

.step-description {
  margin: 0;
  color: var(--text-color-secondary);
  font-size: 1rem;
}

.readonly-banner {
  margin: 0;
}

.readonly-banner :deep(.p-message-wrapper) {
  padding: 1rem;
}

.readonly-banner .banner-content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.readonly-banner .banner-content strong {
  font-size: 1rem;
  color: #b7a026;
}

.readonly-banner .banner-content p {
  margin: 0;
  color: #6c757d;
  font-size: 0.9rem;
}

.readonly-banner :deep(.pi-lock) {
  color: #b7a026;
}

.rule-summary {
  padding: 1.25rem;
  background: var(--surface-0);
  border: 1px solid var(--surface-border);
  border-radius: 8px;
}

.summary-header,
.action-buttons {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--surface-border);
}

.summary-header i,
.action-buttons i {
  font-size: 1.25rem;
  color: var(--primary-color);
}

.summary-header h3 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-color);
}

.summary-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.95rem;
}

.summary-label {
  color: var(--text-color-secondary);
  font-weight: 500;
}

.summary-value {
  color: var(--text-color);
  font-weight: 600;
  text-align: right;
}

.summary-value.amount {
  font-size: 1.125rem;
  color: var(--primary-color);
}

.rule-type-badge {
  padding: 0.25rem 0.75rem;
  background: var(--primary-color);
  color: white;
  border-radius: 4px;
  font-size: 0.875rem;
}

.summary-details {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.75rem;
  background: var(--surface-50);
  border-radius: 6px;
}

.summary-brackets {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.summary-brackets .summary-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-color);
}

.brackets-list {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.bracket-item {
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0.75rem;
  background: var(--surface-0);
  border-radius: 4px;
  font-size: 0.875rem;
}

.bracket-range {
  color: var(--text-color);
}

.bracket-rate {
  font-weight: 700;
  color: var(--primary-color);
}

.summary-description {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem;
  background: var(--surface-50);
  border-radius: 6px;
}

.summary-description .summary-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-color);
}

.description-content {
  font-size: 0.9rem;
  color: var(--text-color);
  line-height: 1.5;
}

.action-buttons {
  display: flex;
  gap: 0.75rem;
  padding-top: 1rem;
  border-top: 1px solid var(--surface-border);
  border-bottom: none;
  justify-content: flex-end;
}

@media (max-width: 768px) {
  .summary-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }
}
</style>
